<script setup>
import {
  WAutocomplete,
  WLabel,
  WModal,
  WParagraph
} from '@alice-health/wonderland-vue'
import uniq from 'lodash/uniq'
import { computed, onMounted, ref, watch } from 'vue'

import { LoadingComponent } from '@/components/shared/atoms'

const props = defineProps({
  isLoadingSuggestedTheme: { type: Boolean, required: true },
  suggestedTheme: { type: String, required: true },
  contactThemes: {
    type: Array,
    default: () => []
  }
})

const categoryThemes = ref([])
const contactReasons = ref([])
const selectedContactReason = ref(undefined)
const selectedCategoryTheme = ref(undefined)
const dialogOpenedAt = ref(undefined)
const emit = defineEmits(['onClose', 'loadContactThemes'])

const handleDialogOpenedAt = () => {
  dialogOpenedAt.value = new Date()
}

const handleOnClose = event => {
  emit('onClose', {
    detail: event.detail,
    dialogOpenedAt: dialogOpenedAt.value,
    selectedCategoryTheme: selectedCategoryTheme.value,
    selectedContactReason: selectedContactReason.value
  })
}

const onSelectedCategoryTheme = event => {
  selectedCategoryTheme.value = event.detail
  updateContactReasons()
}

const onSelectedContactReason = event => {
  selectedContactReason.value = event.detail
}

const updateContactReasons = () => {
  const filteredContactThemes = props.contactThemes.filter(
    contactTheme => contactTheme.categoryTheme === selectedCategoryTheme.value
  )

  contactReasons.value = filteredContactThemes.map(
    contactTheme => contactTheme.contactReason
  )

  selectedContactReason.value = ''
}

onMounted(() => {
  loadContactThemes()
})

watch(
  () => props.contactThemes,
  contactTheme => {
    categoryThemes.value = uniq(
      contactTheme?.map(contactTheme => contactTheme.categoryTheme)
    )
  }
)

watch(
  () => props.suggestedTheme,
  newSuggestedTheme => {
    if (newSuggestedTheme) {
      selectedCategoryTheme.value = newSuggestedTheme
      updateContactReasons()
    }
  },
  { immediate: true }
)

const isFormInvalid = computed(() => {
  return !isCategoryThemeSelected.value || !isContactReasonSelected.value
})

const isCategoryThemeSelected = computed(() => {
  return props.contactThemes.some(
    contactTheme => contactTheme.contactReason === selectedContactReason.value
  )
})

const isContactReasonSelected = computed(() => {
  return props.contactThemes.some(
    contactTheme => contactTheme.categoryTheme === selectedCategoryTheme.value
  )
})

const loadContactThemes = () => {
  emit('loadContactThemes')
}
</script>

<template>
  <WModal
    id="modal"
    data-testid="modal-dialog"
    title="Deseja encerrar esse canal?"
    confirm-label="Encerrar Canal"
    cancel-label="Cancelar"
    icon="icAlertCircleFilled"
    dismiss
    :invalid="isFormInvalid"
    @w-closed="handleOnClose"
    @w-opened="handleDialogOpenedAt"
  >
    <LoadingComponent :loading="isLoadingSuggestedTheme">
      <div class="modal-expand__container">
        <WParagraph>
          Antes de encerrar, faça a classificação do atendimento. Lembre-se: uma
          vez encerrado, o canal não poderá ser reaberto.
        </WParagraph>
        <div class="modal-expand__field">
          <WLabel size="large">Tema do atendimento</WLabel>
          <WAutocomplete
            v-model="selectedCategoryTheme"
            :items="categoryThemes"
            placeholder="Selecione o tema do atendimento"
            @w-change="onSelectedCategoryTheme"
          />
        </div>
        <div class="modal-expand__field">
          <WLabel size="large">Motivo do contato</WLabel>
          <WAutocomplete
            v-model="selectedContactReason"
            placeholder="Selecione o motivo do contato"
            :items="contactReasons"
            @w-change="onSelectedContactReason"
          />
        </div>
      </div>
    </LoadingComponent>
  </WModal>
</template>

<style lang="scss" scoped>
.modal-expand {
  &__container {
    display: flex;
    flex-direction: column;
    gap: var(--gl-spacing-06);
    justify-content: space-between;
    max-width: 540px;
  }

  &__field {
    display: flex;
    flex-direction: column;
  }
}
</style>
